/**
 * Secret Santa Assignment Page
 * Shows the user who they're giving a gift to
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { UserMenu } from '@/components/auth/UserMenu'
import { Button } from '@/components/common/Button'
import { getMyAssignment } from '@/services/secretSanta.service'
import { useSecretSanta } from '@/hooks/useSecretSanta'
import { getSecretSantaByToken } from '@/services/secretSanta.service'
import toast from 'react-hot-toast'

export const SecretSantaAssignment = () => {
  const { accessToken } = useParams<{ accessToken: string }>()
  const { user } = useAuth()
  const [santaId, setSantaId] = useState<string | undefined>(undefined)
  useSecretSanta(santaId)
  const [assignment, setAssignment] = useState<{
    receiverName: string
    receiverWishlistId: string | null
    eventTitle: string
    eventDate: any
    budget: number | null
    currency: string
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!accessToken) return

    const fetchEvent = async () => {
      try {
        const eventData = await getSecretSantaByToken(accessToken)
        setSantaId(eventData.id)
      } catch (err) {
        console.error('Error loading event:', err)
        toast.error('Failed to load Secret Santa event')
      }
    }

    fetchEvent()
  }, [accessToken])

  useEffect(() => {
    if (!santaId || !user) return

    const fetchAssignment = async () => {
      try {
        setLoading(true)
        const data = await getMyAssignment(santaId)
        setAssignment(data)
      } catch (err: any) {
        console.error('Error fetching assignment:', err)
        setError(err.message || 'Failed to load assignment')
      } finally {
        setLoading(false)
      }
    }

    fetchAssignment()
  }, [santaId, user])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your assignment...</p>
        </div>
      </div>
    )
  }

  if (error || !assignment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Assignment Not Available</h2>
          <p className="text-gray-600 mb-6">
            {error || 'You may not be a participant in this event, or names have not been drawn yet.'}
          </p>
          <Link to={`/secret-santa/${accessToken}`} className="text-blue-600 hover:text-blue-700 font-medium">
            Back to Event
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Paiseh!
            </Link>
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Assignment Card */}
        <div className="bg-white rounded-2xl shadow-lg border-2 border-purple-200 p-8 mb-6">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{assignment.eventTitle}</h1>
            <p className="text-gray-600">Your Secret Santa Assignment</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 text-center mb-6">
            <p className="text-lg text-gray-700 mb-3">You are giving a gift to:</p>
            <p className="text-4xl font-bold text-purple-600 mb-6">{assignment.receiverName}</p>

            {assignment.budget && (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                <span className="font-medium">Budget: {assignment.currency} ${(assignment.budget / 100).toFixed(2)}</span>
              </div>
            )}
          </div>

          {assignment.receiverWishlistId ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="font-medium text-green-900 mb-1">Wishlist Available</p>
                  <p className="text-sm text-green-700">
                    {assignment.receiverName} has linked a wishlist. Check it out for gift ideas!
                  </p>
                </div>
              </div>

              <Link to={`/wishlists/${assignment.receiverWishlistId}`} className="block">
                <Button className="w-full">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  View {assignment.receiverName}'s Wishlist
                </Button>
              </Link>
            </div>
          ) : (
            <div className="flex items-start gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="font-medium text-gray-900 mb-1">No Wishlist Linked</p>
                <p className="text-sm text-gray-600">
                  {assignment.receiverName} hasn't linked a wishlist yet. You'll need to find the perfect gift on your own!
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Privacy Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-medium text-yellow-900 mb-1">Keep it Secret!</p>
              <p className="text-sm text-yellow-700">
                Don't tell {assignment.receiverName} that you're their Secret Santa. That's the fun part!
              </p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-6 text-center">
          <Link
            to={`/secret-santa/${accessToken}`}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Event
          </Link>
        </div>
      </main>
    </div>
  )
}
